import React, { useState, useEffect } from 'react';

// Import the JSON-based configuration
import appConfig, { ENDPOINT_KEYS } from './config/appConfig';


const CreateIntegrationFieldDialog = ({
  isOpen,
  onClose,
  selectedRequirements,
  corporateAccount,
  currentProject,
  login_token,
  requirementIdWithPrefix
}) => {
  const [fieldName, setFieldName] = useState('');
  const [fieldDescription, setFieldDescription] = useState('');
  const [fieldDataType, setFieldDataType] = useState('');
  const [fieldSize, setFieldSize] = useState('');
  const [fieldOptionalOrMandatory, setFieldOptionalOrMandatory] = useState('Optional');
  const [fieldTransformation, setFieldTransformation] = useState('');
  const [fieldDataValidation, setFieldDataValidation] = useState('');
  const [fieldDataSecurity, setFieldDataSecurity] = useState('');
  const [status, setStatus] = useState('Created');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldStatuses, setFieldStatuses] = useState([]);
  const [isLoadingStatuses, setIsLoadingStatuses] = useState(false);

  // Updated Dialog styles to match the consistent format
  const DialogStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start', // Changed from 'center' to 'flex-start'
      zIndex: 1000,
      paddingTop: '20px', // Add padding from top
      paddingBottom: '20px' // Add padding from bottom
    },
    dialog: {
      backgroundColor: 'white',
      padding: '20px',
      borderRadius: '8px',
      width: '700px',
      maxWidth: '90vw',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
      maxHeight: 'calc(100vh - 40px)', // Changed from 90vh to calc(100vh - 40px) to account for padding
      overflowY: 'auto', // Ensure scrolling is enabled
      position: 'relative' // Add position relative
    }
  };

  // Initialize when dialog opens
  useEffect(() => {
    if (isOpen) {
      // Reset form state
      setFieldName('');
      setFieldDescription('');
      setFieldDataType('');
      setFieldSize('');
      setFieldOptionalOrMandatory('Optional');
      setFieldTransformation('');
      setFieldDataValidation('');
      setFieldDataSecurity('');
      setStatus('Created');
      setError(''); // Reset error when dialog opens
    }
  }, [isOpen, corporateAccount, currentProject]);

  useEffect(() => {
    if (isOpen) {
      fetchFieldStatuses();
    }
  }, [isOpen, corporateAccount, currentProject]);

  const fetchFieldStatuses = async () => {
    setIsLoadingStatuses(true);
    try {
      const response = await fetch(appConfig.getUrl('/get_statuses'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          entity: 'REQUIREMENT',
          search_query: ''
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        const statuses = data.entity_statuses.map(s => s.status);
        setFieldStatuses(statuses);

        // Set default status if none is selected yet
        if (!status && statuses.includes('Created')) {
          setStatus('Created');
        } else if (!status && statuses.length > 0) {
          setStatus(statuses[0]);
        }
      } else {
        throw new Error(data.status_description || 'Failed to fetch statuses');
      }
    } catch (err) {
      console.error('Error fetching statuses:', err);
    } finally {
      setIsLoadingStatuses(false);
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    setError('');

    // Validate form inputs
    if (!fieldName) {
      setError('Please enter a field name');
      setIsLoading(false);
      return;
    }

    if (selectedRequirements.size !== 1) {
      setError('Please select exactly one integration requirement');
      setIsLoading(false);
      return;
    }

    const integrationId = Array.from(selectedRequirements)[0];

    try {
      const response = await fetch(appConfig.getUrl('/add_integration_requirement_field'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          integration_id: integrationId,
          field_name: fieldName,
          field_description: fieldDescription,
          field_data_type: fieldDataType,
          field_size: fieldSize,
          field_optional_or_mandatory: fieldOptionalOrMandatory,
          field_transformation: fieldTransformation,
          field_data_validation: fieldDataValidation,
          field_data_security: fieldDataSecurity,
          status: status
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        // First show the success message (same pattern as other dialogs)
        setError('SUCCESS: Field created successfully');

        // After a delay, close the dialog
        setTimeout(() => {
          onClose(true); // Close dialog with success indicator
        }, 2000);
      } else {
        throw new Error(data.status_description || 'Failed to add integration field');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error adding integration field:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle cancel button click - explicitly pass false to indicate no success
  const handleCancel = () => {
    onClose(false);
  };

  if (!isOpen) return null;

  return (
    <div style={DialogStyles.overlay}>
      <div style={DialogStyles.dialog}>
        {/* Updated header to match the consistent format */}
        <h2 style={{
          backgroundColor: '#CCCCFF',
          textAlign: 'center',
          padding: '10px',
          margin: '-20px -20px 20px -20px',
          borderRadius: '8px 8px 0 0',
          fontSize: '18px',
          fontWeight: 'bold'
        }}>
          Add Integration Field
        </h2>

        {selectedRequirements.size !== 1 ? (
          <div style={{
            marginBottom: '15px',
            padding: '8px',
            borderRadius: '4px',
            backgroundColor: '#fef2f2',
            color: '#dc2626',
            border: '1px solid #ef4444'
          }}>
            Please select exactly one integration requirement
          </div>
        ) : (
          <div style={{
            marginBottom: '20px',
            padding: '15px',
            border: '1px solid #e5e7eb',
            borderRadius: '4px',
            backgroundColor: '#f9fafb'
          }}>
            <h3 style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '10px' }}>Adding field to:</h3>
            <p style={{ fontSize: '14px', margin: '0' }}>
              Integration Requirement: <strong>{requirementIdWithPrefix}</strong>
            </p>
          </div>
        )}

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Field Name: <span style={{ color: '#dc2626' }}>*</span>
          </label>
          <input
            type="text"
            value={fieldName}
            onChange={(e) => setFieldName(e.target.value)}
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              backgroundColor: selectedRequirements.size !== 1 ? '#f9fafb' : 'white'
            }}
            required
            disabled={selectedRequirements.size !== 1}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Field Description:
          </label>
          <textarea
            value={fieldDescription}
            onChange={(e) => setFieldDescription(e.target.value)}
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              resize: 'vertical',
              backgroundColor: selectedRequirements.size !== 1 ? '#f9fafb' : 'white'
            }}
            rows={3}
            placeholder="Describe the purpose of this field"
            disabled={selectedRequirements.size !== 1}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Data Type:
            </label>
            <select
              value={fieldDataType}
              onChange={(e) => setFieldDataType(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                backgroundColor: selectedRequirements.size !== 1 ? '#f9fafb' : 'white'
              }}
              disabled={selectedRequirements.size !== 1}
            >
              <option value="">Select data type</option>
              <option value="String">String</option>
              <option value="Integer">Integer</option>
              <option value="Float">Float</option>
              <option value="Boolean">Boolean</option>
              <option value="Date">Date</option>
              <option value="DateTime">DateTime</option>
              <option value="Binary">Binary</option>
              <option value="JSON">JSON</option>
              <option value="XML">XML</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Field Size:
            </label>
            <input
              type="text"
              value={fieldSize}
              onChange={(e) => setFieldSize(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                backgroundColor: selectedRequirements.size !== 1 ? '#f9fafb' : 'white'
              }}
              placeholder="e.g. 255, 10.2, MAX"
              disabled={selectedRequirements.size !== 1}
            />
          </div>
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Optional or Mandatory:
          </label>
          <select
            value={fieldOptionalOrMandatory}
            onChange={(e) => setFieldOptionalOrMandatory(e.target.value)}
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              backgroundColor: selectedRequirements.size !== 1 ? '#f9fafb' : 'white'
            }}
            disabled={selectedRequirements.size !== 1}
          >
            <option value="Optional">Optional</option>
            <option value="Mandatory">Mandatory</option>
          </select>
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Transformation Rules:
          </label>
          <textarea
            value={fieldTransformation}
            onChange={(e) => setFieldTransformation(e.target.value)}
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              resize: 'vertical',
              backgroundColor: selectedRequirements.size !== 1 ? '#f9fafb' : 'white'
            }}
            rows={2}
            placeholder="Describe any data transformations"
            disabled={selectedRequirements.size !== 1}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Data Validation:
          </label>
          <textarea
            value={fieldDataValidation}
            onChange={(e) => setFieldDataValidation(e.target.value)}
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              resize: 'vertical',
              backgroundColor: selectedRequirements.size !== 1 ? '#f9fafb' : 'white'
            }}
            rows={2}
            placeholder="Describe validation rules"
            disabled={selectedRequirements.size !== 1}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Data Security:
          </label>
          <textarea
            value={fieldDataSecurity}
            onChange={(e) => setFieldDataSecurity(e.target.value)}
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              resize: 'vertical',
              backgroundColor: selectedRequirements.size !== 1 ? '#f9fafb' : 'white'
            }}
            rows={2}
            placeholder="Describe security requirements"
            disabled={selectedRequirements.size !== 1}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Status:
          </label>
          {isLoadingStatuses ? (
            <div style={{
              width: '100%',
              padding: '8px',
              backgroundColor: '#f9fafb',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              color: '#6b7280'
            }}>
              Loading statuses...
            </div>
          ) : (
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                backgroundColor: selectedRequirements.size !== 1 ? '#f9fafb' : 'white'
              }}
              disabled={selectedRequirements.size !== 1}
            >
              <option value="">Select status</option>
              {fieldStatuses.map(statusOption => (
                <option key={statusOption} value={statusOption}>
                  {statusOption}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Updated error display to match the consistent format */}
        {error && (
          <div style={{
            marginBottom: '15px',
            padding: '8px',
            borderRadius: '4px',
            backgroundColor: error.toLowerCase().includes('success') ? '#f0fdf4' : '#fef2f2',
            color: error.toLowerCase().includes('success') ? '#059669' : '#dc2626',
            border: error.toLowerCase().includes('success') ? '1px solid #10b981' : '1px solid #ef4444'
          }}>
            {error}
          </div>
        )}

        {/* Updated button section to match the consistent format */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
          <button
            onClick={handleCancel}
            style={{
              backgroundColor: '#FFFDD0',
              padding: '8px 16px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
            disabled={isLoading}
          >
            Close
          </button>
          <button
            onClick={handleSubmit}
            style={{
              padding: '8px 16px',
              backgroundColor: selectedRequirements.size !== 1 ? '#e5e7eb' : '#C8F4DE',
              color: selectedRequirements.size !== 1 ? '#6b7280' : 'black',
              border: 'none',
              borderRadius: '4px',
              cursor: selectedRequirements.size !== 1 ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              opacity: isLoading ? 0.5 : 1,
              display: 'flex',
              alignItems: 'center'
            }}
            disabled={isLoading || selectedRequirements.size !== 1}
          >
            {isLoading && (
              <svg style={{
                animation: 'spin 1s linear infinite',
                marginLeft: '-4px',
                marginRight: '8px',
                width: '16px',
                height: '16px'
              }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {isLoading ? 'Adding...' : 'Add Field'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateIntegrationFieldDialog;